import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFolder,
  createFolder,
  deleteFolder,
  renameFolder,
  moveFolder,
} from '@/services/api/folderService';
import { FolderResponse, AddFolderResponse } from '@/types/folders/folder';

export const folderKeys = {
  all: ['folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  list: (params: any) => [...folderKeys.lists(), params] as const,
};

export const useFolders = (
  params: any = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: folderKeys.list(params),
    queryFn: async () => {
      const response = await getFolder(params);
      return response;
    },
    enabled: options?.enabled !== false,
  });
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createFolder(data);
    },
    onSuccess: (response: AddFolderResponse, variables: any) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });

      if (variables.parent_id) {
        queryClient.invalidateQueries({
          queryKey: folderKeys.list({ folder_id: variables.parent_id }),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: folderKeys.list({}),
        });
      }
    },
  });
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await deleteFolder(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
};

export const useRenameFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: any) => {
      return await renameFolder(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
};

export const useMoveFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await moveFolder(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
};
